import { useState } from 'react';
import clsx from 'clsx';
import { Pencil } from 'lucide-react';

/**
 * Design System · InlineEditable (select)
 * Exibe um valor; ao clicar vira <select> ali mesmo. Nada trancado.
 * Usado nos campos de contexto do card (Solicitante, Prazo, Distribuição…).
 * `pending` pinta o valor de amarelo (sinaliza algo faltando).
 */

interface Option {
  value: string;
  label: string;
}

interface InlineEditableProps {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  /** rótulo do valor exibido (se diferente do value). */
  display?: string;
  pending?: boolean;
  disabled?: boolean;
  className?: string;
}

export function InlineEditable({ value, options, onChange, display, pending, disabled, className }: InlineEditableProps) {
  const [editing, setEditing] = useState(false);

  if (editing && !disabled) {
    return (
      <select
        autoFocus
        className="input py-1.5 text-sm w-full !border-blue"
        value={value}
        onChange={(e) => { onChange(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  const shown = display ?? options.find((o) => o.value === value)?.label ?? value;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      className={clsx(
        'group/ie inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 -ml-2 text-sm font-semibold',
        'hover:border-border hover:bg-panel-3 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:border-transparent',
        pending ? 'text-yellow' : 'text-text',
        className,
      )}
    >
      {pending && <span className="w-2 h-2 rounded-full bg-yellow" />}
      <span>{shown}</span>
      {!disabled && <Pencil className="w-3 h-3 text-muted opacity-0 group-hover/ie:opacity-100 ml-auto" />}
    </button>
  );
}
